import { invokeApi } from '../bl_libs/invokeApi';

/**
 * The Powered by DQ sign-up chatbot (/powered-by-dq/chatbot) and its status page (/pbdq/status)
 * — PUBLIC endpoints, no login.
 * my-dq-api: services/common/pbdq_signup.service.js.
 *
 * Every call after the application is created carries its `reference_no` and the
 * `token` it was created with (kept in sessionStorage by the page), or the token of
 * an email link.
 *
 * Multipart uploads set `Content-Type: undefined` so the browser writes the boundary,
 * and are left unencrypted because e2ee would reduce FormData to "{}".
 */

const BASE = '/dq_insider/public/pbdq';

const get = (path, queryParams) => invokeApi({ path: `${BASE}${path}`, method: 'GET', queryParams });
const post = (path, postData) => invokeApi({ path: `${BASE}${path}`, method: 'POST', postData });
const upload = (path, fields, file) => {
    const formData = new FormData();
    if (file) formData.append('media', file);
    Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') formData.append(k, v); });
    return invokeApi({ path: `${BASE}${path}`, method: 'POST', headers: { 'Content-Type': undefined }, postData: formData });
};

// GET — the flow's fixed options, bank transfer availability, the Stripe publishable key.
export const getSignupCatalogService = () => get('/catalog');

// POST — the DS-5 order for a deployment. { solution, deployment }
export const quoteSignupOrderService = (solution, deployment) => post('/quote', { solution, deployment });

// POST — start an application. → { reference_no, access_token, application }
export const startSignupApplicationService = (payload) => post('/applications', payload);

// ── The DQ Pro V2 sign-up page (/signup), the chat's continuation ────────────

// POST — the information page's own check, by the agent's rules. → { errors: string[] }
// direct_order: also report an email that already has an account (a direct order
// creates one at payment, and that would fail).
export const validateSignupDetailsService = (registry, legal_entity = false, direct_order = false) =>
    post('/signup/validate', { registry, legal_entity, direct_order });

// POST — start an application from the sign-up page: the same body as
// startSignupApplicationService; type may also be 'account_request' (the page opened
// directly). → { reference_no, access_token, application } — then the services below.
export const startSignupPageApplicationService = (payload) => post('/signup/applications', payload);

// POST — the status page: an application's status for its reference and contact email.
// → { application, actions: { pay, upload_documents }, token }
export const lookupSignupStatusService = (reference_no, email) => post('/applications/lookup', { reference_no, email });

// GET — the application as it now stands.
export const getSignupApplicationService = (reference_no, token) => get('/applications/status', { reference_no, token });

// POST — DP-7 screening answer.
export const saveSignupScreeningService = (reference_no, token, intended_use) => post('/applications/screening', { reference_no, token, intended_use });

// POST — DP-8 checklist.
export const submitSignupChecklistService = (reference_no, token, checklist) => post('/applications/checklist', { reference_no, token, checklist });

// POST (multipart) — one document: a file and/or a link.
export const uploadSignupDocumentService = (reference_no, token, { doc_type, doc_type_other, link, note }, file) =>
    upload('/applications/documents', { reference_no, token, doc_type, doc_type_other, link, note }, file);

// POST — submit a verification request once its documents are added.
export const submitSignupVerificationService = (reference_no, token, note) => post('/applications/submit', { reference_no, token, note });

// POST — pay by card. → { client_secret, item, wallets_enabled }
export const startSignupCardPaymentService = (reference_no, token) => post('/applications/pay/card', { reference_no, token });

// POST — DS-5 after Go back: change an unpaid direct order that Proceed to Payment
// already saved (same reference). { solution, registry, deployment } → { application }
export const reviseSignupOrderService = (reference_no, token, { solution, registry, deployment }) =>
    post('/applications/revise', { reference_no, token, solution, registry, deployment });

// POST — pay by bank transfer. → { order, instructions }
export const startSignupWirePaymentService = (reference_no, token, currency) => post('/applications/pay/wire', { reference_no, token, ...(currency ? { currency } : {}) });

// POST (multipart) — proof of the bank transfer.
export const uploadSignupWireProofService = (reference_no, token, file, note) =>
    upload('/applications/pay/wire/proof', { reference_no, token, note }, file);

// GET — after a card payment returns: sync with Stripe and read the application.
export const getSignupPaymentStatusService = (reference_no, token, session_id) =>
    get('/applications/pay/status', { reference_no, token, session_id });

// POST — resend the workspace activation email.
export const resendSignupActivationService = (reference_no, token) => post('/applications/resend_activation', { reference_no, token });
